import React, { useEffect, useMemo } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import {
  LayoutFactory,
  PageLayoutType,
} from '../../../core/layout/LayoutFactory';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  authTokenState,
  roleAccessState,
} from '../../../core/atoms/auth/auth.atom';
import { routes } from '../../routes/routes';
import LazyLoad from '../../../core/common/LazyLoading/LazyLoading';
import NotFoundPage from '../../../core/pages/error/NotFoundPage/NotFoundPage';
import { useMutation } from '@tanstack/react-query';
import axios, { SuccessResponse } from '../../../core/libraries/axios/axios';
import { MappedRoleAccess, RoleType } from '../../../core/domain/Role';
import { DefaultError } from '@tanstack/query-core/build/modern/index';
import { apiRoutes } from '../../api/apiRoutes';
import {
  ProgressFactory,
  ProgressVariant,
} from '../../../core/common/Progress/ProgressFactory';

/* eslint-disable new-cap */
const Dashboard = LazyLoad(
  () => import('../../../core/pages/admin/DashboardPage/DashboardPage'),
);
const UsersPage = LazyLoad(
  () => import('../../../core/pages/admin/User/UsersPage/UsersPage'),
);
const UserCreatePage = LazyLoad(
  () => import('../../../core/pages/admin/User/UserCreatePage/UserCreatePage'),
);
const UserEditPage = LazyLoad(
  () => import('../../../core/pages/admin/User/UserEditPage/UserEditPage'),
);
const TranslationsPage = LazyLoad(
  () =>
    import(
      '../../../core/pages/admin/Translation/TranslationsPage/TranslationsPage'
    ),
);
const ProfilePage = LazyLoad(
  () => import('../../../core/pages/admin/Profile/ProfilePage/ProfilePage'),
);
const SettingsPage = LazyLoad(
  () => import('../../../core/pages/admin/Settings/SettingsPage/SettingsPage'),
);

const LoginPage = LazyLoad(
  () => import('../../../app/pages/public/PublicLoginPage/PublicLoginPage'),
);
const PasswordResetPage = LazyLoad(
  () =>
    import('../../../core/pages/public/PasswordResetPage/PasswordResetPage'),
);
const PasswordResetConfirmationPage = LazyLoad(
  () =>
    import(
      '../../../core/pages/public/PasswordResetConfirmationPage/PasswordResetConfirmationPage'
    ),
);
const RegistrationConfirmationPage = LazyLoad(
  () =>
    import(
      '../../../core/pages/public/RegistrationConfirmationPage/RegistrationConfirmationPage'
    ),
);
const DeletionConfirmationPage = LazyLoad(
  () =>
    import(
      '../../../core/pages/public/DeletionConfirmationPage/DeletionConfirmationPage'
    ),
);

const PrivacyPolicyPage = LazyLoad(
  () =>
    import('../../../core/pages/public/PrivacyPolicyPage/PrivacyPolicyPage'),
);

const TermsOfServicePage = LazyLoad(
  () =>
    import('../../../core/pages/public/TermsOfServicePage/TermsOfServicePage'),
);

const RegisterPage = LazyLoad(
  () =>
    import('../../../app/pages/public/PublicRegisterPage/PublicRegisterPage'),
);

const UserVerifyPage = LazyLoad(
  () => import('../../../app/pages/public/UserVerifyPage/UserVerifyPage'),
);

const HelpPage = LazyLoad(
  () => import('../../../app/pages/admin/HelpPage/HelpPage'),
);

const VocabularyPage = LazyLoad(
  () => import('../../../app/pages/admin/VocabularyPage/VocabularyPage'),
);

const ClientsPage = LazyLoad(
  () => import('../../../app/pages/admin/Client/ClientsPage/ClientsPage'),
);

const ClientCreatePage = LazyLoad(
  () =>
    import('../../../app/pages/admin/Client/ClientCreatePage/ClientCreatePage'),
);

const ClientEditPage = LazyLoad(
  () => import('../../../app/pages/admin/Client/ClientEditPage/ClientEditPage'),
);

const PlanPage = LazyLoad(
  () => import('../../../app/pages/admin/Plan/PlanPage/PlanPage'),
);

const PlanSuccessPage = LazyLoad(
  () => import('../../../app/pages/admin/Plan/PlanSuccessPage/PlanSuccessPage'),
);

const ClientProfilePage = LazyLoad(
  () =>
    import(
      '../../../app/pages/admin/Client/ClientProfilePage/ClientProfilePage'
    ),
);

const DocumentsPage = LazyLoad(
  () => import('../../../app/pages/admin/Document/DocumentsPage/DocumentsPage'),
);

const DocumentFormPage = LazyLoad(
  () =>
    import(
      '../../../app/pages/admin/Document/DocumentFormPage/DocumentFormPage'
    ),
);
/* eslint-enable new-cap */

export const RouteProvider = () => {
  const authStateValue = useRecoilValue(authTokenState);
  const setRoleAccessStateValue = useSetRecoilState(roleAccessState);

  const { mutate: getMappedRoleAccess, isPending } = useMutation<
    SuccessResponse<MappedRoleAccess[]>,
    DefaultError
  >({
    mutationFn: () => axios.get(apiRoutes.roles.mappedAccess),
    onSuccess: (response) => {
      setRoleAccessStateValue(response.data);
    },
  });

  useEffect(() => {
    if (authStateValue) {
      getMappedRoleAccess();

      if (
        authStateValue.roles.includes(RoleType.USER) ||
        authStateValue.roles.includes(RoleType.USER_LIMITED)
      ) {
        postTokenToExtension(authStateValue.token);
      }
    } else {
      postLogOutToExtension();
    }
  }, [authStateValue]);

  // Sends only the web app's ACCESS token. The extension trades it for its own
  // independent token pair (see `auth/extension-token`) rather than reusing the
  // web app's refresh token, so the two clients never rotate the same token.
  const postTokenToExtension = (token: string) => {
    const editorExtensionIds =
      process.env.REACT_APP_GRAMMAR_EXTENSION_ID?.split(',') ?? [];

    for (const editorExtensionId of editorExtensionIds) {
      try {
        // @ts-ignore
        if (chrome && chrome.runtime) {
          // @ts-ignore
          chrome.runtime.sendMessage(editorExtensionId, {
            type: 'grmrAuthToken',
            token: token,
          });
        }
      } catch (e) {
        console.log(e);
      }
    }
  };

  const postLogOutToExtension = () => {
    const editorExtensionIds =
      process.env.REACT_APP_GRAMMAR_EXTENSION_ID?.split(',') ?? [];

    for (const editorExtensionId of editorExtensionIds) {
      try {
        // @ts-ignore
        if (chrome && chrome.runtime) {
          // @ts-ignore
          chrome.runtime.sendMessage(editorExtensionId, {
            type: 'grmrAuthLogout',
          });
        }
      } catch (e) {
        console.log(e);
      }
    }
  };

  const adminPages = useMemo(() => {
    if (authStateValue) {
      return (
        <>
          <Route path={routes.admin.dashboard} element={<Dashboard />} />
          <Route path={routes.homepage} element={<Dashboard />} />
          <Route path={routes.admin.users.list} element={<UsersPage />} />
          <Route path={routes.admin.users.new} element={<UserCreatePage />} />
          <Route path={routes.admin.users.edit} element={<UserEditPage />} />
          <Route
            path={routes.admin.profile}
            element={
              authStateValue.roles.includes(RoleType.USER) ? (
                <ClientProfilePage />
              ) : (
                <ProfilePage />
              )
            }
          />
          <Route path={routes.admin.settings} element={<SettingsPage />} />
          <Route path={routes.admin.help} element={<HelpPage />} />
          <Route path={routes.admin.vocabulary} element={<VocabularyPage />} />
          <Route
            path={routes.admin.documents.list}
            element={<DocumentsPage />}
          />
          <Route
            path={routes.admin.documents.create}
            element={<DocumentFormPage />}
          />
          <Route
            path={routes.admin.documents.edit}
            element={<DocumentFormPage />}
          />
          <Route path={routes.admin.clients.list} element={<ClientsPage />} />
          <Route
            path={routes.admin.clients.new}
            element={<ClientCreatePage />}
          />
          <Route
            path={routes.admin.clients.edit}
            element={<ClientEditPage />}
          />
          <Route
            path={routes.admin.translations.list}
            element={<TranslationsPage />}
          />
          <Route path={routes.admin.plans} element={<PlanPage />} />
          <Route
            path={routes.admin.purchaseCallback}
            element={<PlanSuccessPage />}
          />
          <Route path="*" element={<NotFoundPage />} />
        </>
      );
    }

    return <></>;
  }, [authStateValue]);

  const authPages = useMemo(() => {
    if (authStateValue) {
      return (
        <>
          <Route
            path={routes.auth.registrationConfirmation}
            element={<RegistrationConfirmationPage />}
          />
          <Route
            path={routes.auth.deletionConfirmation}
            element={<DeletionConfirmationPage />}
          />
        </>
      );
    }

    return (
      <>
        <Route
          path={routes.auth.registrationConfirmation}
          element={<RegistrationConfirmationPage />}
        />
        <Route
          path={routes.auth.deletionConfirmation}
          element={<DeletionConfirmationPage />}
        />
        <Route
          path={routes.auth.passwordReset}
          element={<PasswordResetPage />}
        />
        <Route
          path={routes.auth.passwordResetConfirmation}
          element={<PasswordResetConfirmationPage />}
        />
        <Route path={routes.auth.login} element={<LoginPage />} />
        <Route path={routes.auth.register} element={<RegisterPage />} />
        <Route path={routes.auth.verify} element={<UserVerifyPage />} />
        <Route path={routes.homepage} element={<LoginPage />} />
        <Route path="*" element={<Navigate to={routes.auth.login} replace />} />
      </>
    );
  }, [authStateValue]);

  const publicPages = useMemo(() => {
    return (
      <>
        <Route
          path={routes.public.privacyPolicy}
          element={<PrivacyPolicyPage />}
        />
        <Route
          path={routes.public.termsOfService}
          element={<TermsOfServicePage />}
        />
      </>
    );
  }, []);

  if (authStateValue && isPending) {
    return <ProgressFactory variant={ProgressVariant.linear} />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<LayoutFactory variant={PageLayoutType.admin} />}>
          {adminPages}
        </Route>
        <Route element={<LayoutFactory variant={PageLayoutType.auth} />}>
          {authPages}
        </Route>
        <Route element={<LayoutFactory variant={PageLayoutType.public} />}>
          {publicPages}
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export default RouteProvider;
